import { useRef, useState } from 'react';
import {
  MenuItem,
  Button,
  ButtonClassKey,
  IconClassKey,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  PaperClassKey,
  SvgIconTypeMap,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { memo } from 'react';
import { get } from 'lodash';
import clsx from 'clsx';
import { useWhyDidYouUpdate } from 'hooks';
import { useStyles } from './style';
import { useTranslation } from 'react-i18next';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';

type KeyType<T> = Extract<keyof T, string>;

type TruncateProps<T> = T extends string
  ? { labelField?: undefined; valueField?: undefined }
  : { labelField: KeyType<T>; valueField: KeyType<T> };

type MainProps<T> = {
  name?: string;
  value: T | null;
  options: T[];
  disabled?: boolean;
  label?: string;
  cls?: {
    paper?: {
      [key in PaperClassKey]?: string;
    };
    btn?: {
      [key in ButtonClassKey]?: string;
    };
    icon?: {
      [key in IconClassKey]?: string;
    };
    select?: string;
    label?: string;
    value?: string;
    root?: string;
    listItem?: string;
  };
  onSelect: (item: T, name?: string) => void;
  withButton?: boolean;
  parentWidth?: boolean;
  withTranslation?: boolean;
  ArrowIcon?: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
};

type SelectProps<T> = MainProps<T> & TruncateProps<T>;

function Select<T>({
  name,
  value,
  options,
  labelField,
  valueField,
  label,
  onSelect,
  disabled = false,
  withButton = true,
  parentWidth = false,
  withTranslation = false,
  cls,
  ArrowIcon = KeyboardArrowDownIcon,
}: React.PropsWithChildren<SelectProps<T>>) {
  useWhyDidYouUpdate('Select', {
    value,
    options,
    labelField,
    valueField,
    label,
    onSelect,
    disabled,
    withButton,
    cls,
  });
  const { t } = useTranslation();
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  };

  const selectItem = (event: React.MouseEvent<EventTarget>, option: T) => {
    onSelect(option, name);
    handleClose(event);
  };

  return (
    <>
      <div
        ref={anchorRef}
        onClick={disabled ? undefined : handleToggle}
        className={clsx(classes.inputWrapper, cls?.select, {
          [classes.disabled]: disabled,
        })}
      >
        <span className={clsx(classes.textWrapper, cls?.value)}>
          {withTranslation
            ? t(
                labelField
                  ? (get(value, labelField) as string)
                  : (value as string)
              )
            : labelField
            ? get(value, labelField)
            : value}
        </span>
        {withButton ? (
          <Button
            classes={{
              ...cls?.btn,
              root: clsx(classes.btn, cls?.btn?.root),
            }}
          >
            <ArrowIcon
              classes={{
                ...cls?.icon,
                root: clsx(cls?.icon?.root, classes.icon, {
                  [classes.openIcon]: open,
                }),
              }}
            />
          </Button>
        ) : null}
      </div>
      <Popper
        className={classes.zIndex}
        anchorEl={anchorRef.current}
        open={open}
        role={undefined}
        transition
        disablePortal
        style={{
          width: parentWidth
            ? anchorRef.current?.getBoundingClientRect().width
            : 'auto',
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              zIndex: 9,
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper classes={cls?.paper}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  classes={{
                    root: classes.list,
                  }}
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {options.map((option) => {
                    const isActive =
                      valueField && value
                        ? option[valueField] === value[valueField]
                        : value === option;

                    return (
                      <MenuItem
                        selected={isActive}
                        onClick={(e) => selectItem(e, option)}
                        classes={{
                          root: clsx(classes.listItem, cls?.listItem),
                          gutters: classes.gutters,
                        }}
                      >
                        {withTranslation
                          ? t(
                              labelField
                                ? (get(option, labelField) as string)
                                : (option as string)
                            )
                          : labelField
                          ? get(option, labelField)
                          : option}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default memo(Select);
